/**
 * Campo di testo con suggerimenti: si sceglie una voce esistente o si scrive
 * un valore nuovo.
 *
 * Sostituisce `<datalist>`, che il browser disegna per conto suo, staccato
 * dal campo, e filtra con il testo gia presente: con "Campionato" scritto
 * mostrava solo "Campionato", e le altre voci restavano nascoste finche non
 * si cancellava tutto. Qui la ricerca parte vuota a ogni apertura, quindi
 * l'elenco e sempre completo.
 */

import { useState } from 'react';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { stessoNome } from '@calcio/shared';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export function CampoSuggerito({
  id,
  valore,
  opzioni,
  onCambia,
  segnaposto,
  cerca = 'Cerca o scrivi…',
  nuovo,
  invalido = false,
}: {
  id: string;
  valore: string;
  opzioni: readonly string[];
  onCambia: (valore: string) => void;
  segnaposto: string;
  cerca?: string;
  /** Testo della voce che usa quanto scritto, se non coincide con un'opzione. */
  nuovo: (testo: string) => string;
  invalido?: boolean;
}) {
  const [aperto, setAperto] = useState(false);
  const [ricerca, setRicerca] = useState('');

  const testo = ricerca.trim();
  const esiste = opzioni.some((o) => stessoNome(o, testo));

  const scegli = (v: string) => {
    onCambia(v);
    setAperto(false);
  };

  return (
    <Popover
      open={aperto}
      onOpenChange={(a) => {
        setAperto(a);
        if (a) setRicerca('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={aperto}
          aria-invalid={invalido}
          className={cn('w-full justify-between font-normal', !valore && 'text-muted-foreground')}
        >
          <span className="truncate">{valore || segnaposto}</span>
          <ChevronsUpDownIcon className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder={cerca} value={ricerca} onValueChange={setRicerca} />
          <CommandList>
            <CommandEmpty>Nessun suggerimento.</CommandEmpty>
            {testo && !esiste && (
              <CommandGroup>
                {/* Il valore contiene la ricerca: il filtro lo lascia sempre visibile. */}
                <CommandItem value={`nuovo ${testo}`} onSelect={() => scegli(testo)}>
                  {nuovo(testo)}
                </CommandItem>
              </CommandGroup>
            )}
            {opzioni.length > 0 && (
              <CommandGroup>
                {opzioni.map((o) => (
                  <CommandItem key={o} value={o} onSelect={() => scegli(o)}>
                    {o}
                    <CheckIcon
                      className={cn('ml-auto', stessoNome(o, valore) ? 'opacity-100' : 'opacity-0')}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
